import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import { useDispatch } from 'react-redux';
import { strings } from '../../resources/strings';
import { OrderLogType } from '../../enums/OrderLogType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { GlobalPickerPerformType } from '../../enums/GlobalPickerPerformType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { OrderForListing } from '../../models/OrderForListing';
import { CurrentState } from '../../models/CurrentState';
import { MainActions } from '../../actions/MainActions';
import { SaveStateLogInput } from '../../services/StatesOrders/SaveStateLog/SaveStateLogInput';
import { SaveOrderLogInput } from '../../services/OrderLogs/SaveOrderLog/SaveOrderLogInput';
import { SaveStatePrescriptionLogInput } from '../../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogInput';
import { Button } from '../../common-components/Button/Button';
import { RestrictionHelper } from '../../helpers/RestrictionHelper';
import { RestrictionType } from '../../enums/RestrictionType';

interface IModalUpdateStateForSelectedOrdersByGlobalPickerProps {
  selectedOrders?: OrderForListing[];
  userId?: number;
  globalPickerPerformType: GlobalPickerPerformType;
  close: () => void;
  finish: () => void;
}

const ModalUpdateStateForSelectedOrdersByGlobalPicker: React.FC<IModalUpdateStateForSelectedOrdersByGlobalPickerProps> = ({
  selectedOrders,
  userId,
  globalPickerPerformType,
  close,
  finish
}) => {

  const featureFlag = true;
  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { session } = useAuth();
  const [updating, setUpdating] = React.useState<boolean>(false);
  const [cancelUpdating, setCancelUpdating] = React.useState<boolean>(false);
  const [updatingPercent, setUpdatingPercent] = React.useState<number>(0);
  const [canceled, setCanceled] = React.useState<boolean>(false);

  const updatingError = React.useRef(0);
  const updatingErrorServerNotResponding = React.useRef(0);
  const updatingIndex = React.useRef(0);
  const cancel = React.useRef(false);
  const eligibleOrders = React.useRef<CurrentState[] | null>(null);
  const orderLogParentForRegressionValidation = React.useRef<number | null | undefined>(null);
  const orderLogParentForUpdateOrderStateInBatch = React.useRef<number | null | undefined>(null);

  const {
    alertService,
    saveStateLogService,
    saveStatePrescriptionLogService,
    orderForListingService,
    saveOrderLogService
  } = React.useContext(ServicesContext);

  const orderSelection = React.useMemo(() => {
    return {
      containsAnyPhysicalOrder: selectedOrders?.some(o => o.order.isPhysicalStore),
      containsAnyEcommerceOrder: selectedOrders?.some(o => !o.order.isPhysicalStore),
    }
  }, [selectedOrders])

  const memoHeaderRender = React.useMemo(() => {
    return (
      <Modal.Header closeButton={!updating}>
        <Modal.Title>
          {strings.Perform}
        </Modal.Title>
      </Modal.Header>
    )
  }, [updating]);

  const memoBodyRender = React.useMemo(() => {
    if (updating) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ marginBottom: '10px' }}>
            {globalPickerPerformType === GlobalPickerPerformType.ConfirmSeparation
              ? 'Aguarde, confirmando a separação dos pedidos selecionados...'
              : 'Aguarde...'
            }
          </span>
          <ProgressBar now={updatingPercent} label={`${updatingPercent}%`} variant='success' />
          <div style={{marginTop:'20px', width:'100%', display:'flex', justifyContent:'stretch'}}>
            <Button text={'Cancelar'} width='100%' click={() => cancelOperation()} />
          </div>
        </div>
      );
    }

    if (orderSelection.containsAnyPhysicalOrder && orderSelection.containsAnyEcommerceOrder) {
      return (
        <Alert variant='warning' style={{ textAlign: 'justify' }}>
          Você selecionou pedidos realizados em loja física e pedidos realizados no e-commerce. Você deve evoluir os pedidos separadamente.
        </Alert>
      );
    }

    return (
      <div style={{ display:'flex', justifyContent:'center'}}>
        {globalPickerPerformType === GlobalPickerPerformType.ConfirmSeparation &&
          <Button text={'Confirmação de Separação em Lote'} width='100%' disabled={canceled} click={() => perform()} />
        }
      </div>
    );
  }, [updating, updatingPercent, orderSelection, session, canceled]);

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}
    >
      {memoHeaderRender}

      <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {memoBodyRender}
      </Modal.Body>
    </Modal>
  );

  async function perform() {
    if (RestrictionHelper.hasRestriction(session, RestrictionType.RequestLenses)) {
      alertService.warning(RestrictionHelper.getRestrictionMessage(session, RestrictionType.RequestLenses, strings.RequestLensRestrictionGenericReason));
      return;
    }

    const eligibleList = selectedOrders ? await orderForListingService.getEligibleOrdersForUpdatingPerformedByGlobalPicker(selectedOrders) : [];
    if (!eligibleList || eligibleList.length === 0) {
      close();
      return;
    }

    const confirmResult = await alertService.confirm('Realmente deseja confirmar a separação dos pedidos selecionados?');
    if (!confirmResult.isConfirmed) {
      return;
    }

    orderLogParentForUpdateOrderStateInBatch.current = await getOrderLogParentForUpdateOrderStateInBatch(eligibleList);

    eligibleOrders.current = [...eligibleList];
    console.log('eligible orders to update: ', JSON.stringify(eligibleOrders.current));
    setUpdating(true);
    setCancelUpdating(false);
    updatingError.current = 0;
    updatingIndex.current = 0;
    cancel.current = false;
    updateNextOrder(0);
    mainActions.setNotWait(true);
  }

  async function getOrderLogParentForUpdateOrderStateInBatch(eligibleList: CurrentState[]) {
    const saveOrderLogInput = new SaveOrderLogInput();
    saveOrderLogInput.userId = userId;
    saveOrderLogInput.orderLogType = OrderLogType.UpdateOrderStateInBatch;
    saveOrderLogInput.details = `Confirmação de separação por separador global: ` + eligibleList.map(item => item.number.toString()).join(', ');
    saveOrderLogInput.parentId = orderLogParentForRegressionValidation.current;
    const saveOrderLogOutput = await saveOrderLogService.handle(saveOrderLogInput);
    return saveOrderLogOutput?.value?.result && saveOrderLogOutput?.value?.orderLogId
      ? saveOrderLogOutput.value.orderLogId
      : null;
  }

  function cancelOperation() {
    setCancelUpdating(true);
    cancel.current = true;
    setCanceled(true);
    mainActions.setNotWait(false);
  }

  async function updateNextOrder(orderPosition: number) {
    if (cancelUpdating || cancel.current) {
      setUpdating(false);
      return;
    }

    const orderForListing = selectedOrders && orderPosition < selectedOrders.length ? selectedOrders[orderPosition] : null;

    if (!orderForListing) {
      setUpdatingPercent(100);
      setTimeout(() => finishUpdating(), 1000);
      return;
    }

    const orderEligibleForChange = orderForListing && eligibleOrders.current
      ? eligibleOrders.current.filter(xeo => xeo.orderId === orderForListing.order.id).length > 0
      : false;

    let percent = selectedOrders && selectedOrders.length > 0 ? (orderPosition / selectedOrders.length) * 100 : 0;
    percent = +percent.toFixed(0);

    let updateError = false;
    let serverResponse = false;
    if (orderEligibleForChange) {
      console.log(`order eligible to update: ${orderForListing.order.number}`);

      if (featureFlag) {
        try {
          const saveStatePrescriptionLogInput = new SaveStatePrescriptionLogInput();
          saveStatePrescriptionLogInput.orderId = orderForListing.order.id;
          saveStatePrescriptionLogInput.newStatePrescriptionId = StatePrescriptionIdentification.AguardandoMontagemExterna;
          saveStatePrescriptionLogInput.userId = userId || 0;    
          const saveStatePrescriptionLogResult = await saveStatePrescriptionLogService.handle(saveStatePrescriptionLogInput);
          if (!saveStatePrescriptionLogResult?.value?.result) {
            updateError = true;
          }
        } catch (error: any) {
          serverResponse = error?.response;
          updateError = true;
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (updateError) {
        await saveOrderLogForOrder(orderForListing.order.id, 'Realizada sem sucesso.', orderLogParentForUpdateOrderStateInBatch.current);
      } else {
        await saveOrderLogForOrder(orderForListing.order.id, 'Realizada com sucesso.', orderLogParentForUpdateOrderStateInBatch.current);
      }
    } else {
      console.log(`order ineligible to update: ${orderForListing.order.number}`);
    }

    if (updateError) {
      updatingError.current = updatingError.current + 1;

      if (!serverResponse) updatingErrorServerNotResponding.current = updatingErrorServerNotResponding.current + 1;
    }

    setUpdatingPercent(percent);

    if (orderEligibleForChange) {
      updatingIndex.current = updatingIndex.current + 1;
    }

    updateNextOrder(orderPosition + 1);
  }

  async function saveOrderLogForOrder(orderId: number, details: string, parentId?: number | null) {
    const saveOrderLogInput = new SaveOrderLogInput();
    saveOrderLogInput.userId = userId;
    saveOrderLogInput.orderId = orderId;
    saveOrderLogInput.orderLogType = OrderLogType.UpdateOrderStateInBatch;
    saveOrderLogInput.details = details;
    saveOrderLogInput.parentId = parentId;
    return await saveOrderLogService.handle(saveOrderLogInput);
  }

  async function finishUpdating() {
    console.log('updating finished:');
    const selectedOrdersNumber = selectedOrders?.length || 0;
    console.log(' - selected order number:', selectedOrdersNumber);
    const eligibleOrdersNumber = eligibleOrders.current?.length || 0;
    console.log(' - eligible order number:', eligibleOrdersNumber);
    console.log(' - errors:', updatingError.current);
    console.log(' - errors (server did not respond):', updatingErrorServerNotResponding.current);
    const updatingSuccess = eligibleOrdersNumber - updatingError.current;
    console.log(' - success:', updatingSuccess);
    mainActions.setNotWait(false);
    if (updatingError.current > 0) {
      console.log(' - update finished with error!');
      await alertService.info(
        'A alteração foi concluída. '+
        'Se algum pedido não foi alterado como se espera, tente novamente e se o problema persistir avise o time Tech.'
      );
    } else {
      console.log(' - update finished without error!');
      await alertService.info(strings.UpdatingFinishedWithSuccess);
    }
    setUpdating(false);
    finish();
  }
}

export { ModalUpdateStateForSelectedOrdersByGlobalPicker };